/**
 * Nathalie Robert — monthly evolution advice.
 *
 * Fires on the 28th of every month IF Nathalie has been hired AND at
 * least one actionable insight is found in the live KPIs.
 *
 * Each insight is grounded in real game state — no fictional events.
 * Categories considered (in priority order):
 *
 *   1. Fleet occupancy high & stable → suggest more vehicles
 *   2. Drivers below 50 satisfaction → suggest a retention action
 *   3. Specialty fleet under-used (frigo / ADR / animaux with no
 *      contract in the matching category this month)
 *   4. Cash buildup with no investment (treasury > runway × 6 months)
 *
 * Returns the Messagerie payload OR null when no insight applies
 * (Nathalie doesn't talk for the sake of talking).
 *
 * 31/05/2026 — Nathalie active assistant pattern v2 (recadré).
 */

const TTL_DAYS = 30;

const fmt = (n) => Math.round(n || 0).toLocaleString('fr-FR');

/**
 * @param {object} state  live store state.
 * @returns {object|null} Messagerie payload OR null.
 */
export function buildNathalieMonthlyAdvice(state) {
  if (!state || typeof state !== 'object') return null;
  const hired = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
  if (!hired) return null;

  const insights = [];

  // ── 1. Fleet occupancy ──
  const vehicles = Array.isArray(state.vehicles) ? state.vehicles : [];
  if (vehicles.length > 0) {
    const active = vehicles.filter(v => v.assignedContractId).length;
    const occupancy = active / vehicles.length;
    if (occupancy >= 0.9 && vehicles.length >= 2) {
      insights.push(
        `**🚚 Flotte saturée** : ${active} véhicule${active > 1 ? 's' : ''} en mission sur ${vehicles.length}, ` +
        `soit ${Math.round(occupancy * 100)}% d'occupation. C'est le moment de réfléchir à un véhicule supplémentaire ` +
        `(ou de filtrer les contrats les moins rentables).`,
      );
    } else if (occupancy < 0.5 && vehicles.length >= 3) {
      insights.push(
        `**📉 Flotte sous-utilisée** : seulement ${Math.round(occupancy * 100)}% d'occupation (${active}/${vehicles.length}). ` +
        `Peut-être envisager de céder un véhicule ou de booster la prospection commerciale.`,
      );
    }
  }

  // ── 2. Drivers below 50 satisfaction ──
  const drivers = Array.isArray(state.drivers) ? state.drivers : [];
  const unhappy = drivers.filter(d => (d.satisfaction ?? 75) < 50);
  if (unhappy.length >= 2) {
    const names = unhappy.slice(0, 3).map(d => d.name).join(', ');
    insights.push(
      `**😔 Climat social tendu** : **${unhappy.length} chauffeur${unhappy.length > 1 ? 's' : ''}** sous 50 de satisfaction ` +
      `(${names}${unhappy.length > 3 ? '…' : ''}). Un entretien individuel ou une prime ciblée coûtent ` +
      `infiniment moins cher qu'un remplacement.`,
    );
  }

  // ── 3. Specialty fleet under-used ──
  // Build a set of contract categories run in the active list.
  const activeCategories = new Set(
    (state.activeContracts || []).map(c => c.category).filter(Boolean),
  );
  const specialtyChecks = [
    { spec: 'Frigorifique', label: 'frigo', catRegex: /Frigo|Pharma/i },
    { spec: 'ADR',          label: 'ADR / chimie', catRegex: /ADR|Chimie|Spécialisé/i },
    // 05/02/2026 — Q1 retour user prod : check « bétaillère » retiré.
    { spec: 'BTP lourd',    label: 'porte-engins', catRegex: /BTP/i },
    { spec: 'ZFE',          label: 'électrique ZFE', catRegex: /ZFE|Urbain|Vert/i },
  ];
  for (const check of specialtyChecks) {
    const fleetHas = vehicles.some(v => (v.specialties || []).includes(check.spec));
    const contractMatch = Array.from(activeCategories).some(c => check.catRegex.test(c));
    if (fleetHas && !contractMatch) {
      insights.push(
        `**🎯 Spécialité ${check.label} dormante** : vous avez un véhicule équipé ${check.spec} mais aucun contrat de ` +
        `cette catégorie ce mois-ci. Pensez à filtrer les opportunités sur cette spécialité — c'est là que la marge est.`,
      );
      break; // One specialty insight per message, evite la surcharge.
    }
  }

  // ── 4. Cash buildup with no investment ──
  // Heuristic: treasury > 12× average monthly net result AND > 100k€.
  const monthlyHistory = Array.isArray(state.monthlyHistory) ? state.monthlyHistory : [];
  if (monthlyHistory.length >= 3) {
    const recent = monthlyHistory.slice(-3);
    const avgMonthlyNet = recent.reduce((s, m) => s + (m.netResult || 0), 0) / recent.length;
    if (avgMonthlyNet > 5000 && (state.treasury || 0) > Math.max(100000, avgMonthlyNet * 12)) {
      insights.push(
        `**💰 Trésorerie excédentaire** : **${fmt(state.treasury)} €** en caisse alors que le résultat moyen tourne à ` +
        `${fmt(avgMonthlyNet)} €/mois. Le cash dort. Investissement véhicule, dépôt secondaire ou rachat d'un concurrent ` +
        `pourraient le faire travailler.`,
      );
    }
  }

  if (insights.length === 0) return null;

  const intro = `Bonjour Patron,\n\nQuelques observations de fin de mois — rien d'urgent, juste des pistes :\n\n`;
  const outro = `\n\nÀ vous de voir si ça vaut la peine. Bonne fin de mois !\n\n— Nathalie`;
  const body = intro + insights.join('\n\n') + outro;
  const month = state.month || 1;
  const year = state.year || 1;

  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'advice',
    kind: 'info',
    subject: `Pistes pour le mois prochain — bilan ${String(month).padStart(2, '0')}/${year}`,
    body,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };
}
